import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, PhoneOff, Volume2, VolumeX, 
  User, Headphones, CheckCircle, XCircle, RotateCcw,
  MessageSquare, Loader2, Play, Pause
} from 'lucide-react';

const API = API_BASE;

// Function to shuffle options for a single step and return the new correct index
const shuffleStepOptions = (step) => {
  if (!step.options || step.options.length <= 1) return step;
  
  const options = [...step.options];
  const correctIndex = options.findIndex(opt => opt.correct);
  
  // Create index array and shuffle using Fisher-Yates
  const indices = options.map((_, i) => i);
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  
  // Apply shuffle
  const shuffledOptions = indices.map(i => options[i]);
  
  return {
    ...step,
    options: shuffledOptions
  };
};

// Function to shuffle all steps in a simulation
const shuffleSimulationOptions = (simulation) => {
  if (!simulation || !simulation.steps) return simulation;
  
  return {
    ...simulation,
    steps: simulation.steps.map(step => shuffleStepOptions(step))
  };
};

// Simulation Scenarios with customer dialogues
const SIMULATIONS = {
  'new-customer': {
    id: 'new-customer',
    title_en: 'New Customer Inquiry',
    title_es: 'Consulta de Cliente Nuevo',
    description_en: 'A potential customer calls asking about truck financing.',
    description_es: 'Un cliente potencial llama preguntando sobre financiamiento de camiones.',
    difficulty: 'easy',
    duration: 10,
    icon: '📞',
    steps: [
      {
        type: 'customer',
        audio_en: "Hello? Hi, I'm looking to finance a semi truck for my business. Is this the right place to call?",
        audio_es: "¿Hola? Hola, estoy buscando financiar un camión semi para mi negocio. ¿Es este el lugar correcto para llamar?",
        options: [
          {
            text_en: "Yes, you've reached the right place! Welcome to Inspired Funding. How can I help you today?",
            text_es: "¡Sí, llegaste al lugar correcto! Bienvenido a Inspired Funding. ¿Cómo puedo ayudarte hoy?",
            correct: true,
            feedback_en: "Great greeting! Professional and welcoming.",
            feedback_es: "¡Excelente saludo! Profesional y acogedor."
          },
          {
            text_en: "Yeah. What do you want?",
            text_es: "Sí. ¿Qué quieres?",
            correct: false,
            feedback_en: "Too informal and unwelcoming. Always greet professionally.",
            feedback_es: "Demasiado informal y poco acogedor. Siempre saluda profesionalmente."
          },
          {
            text_en: "Hold on, let me transfer you.",
            text_es: "Espera, déjame transferirte.",
            correct: false,
            feedback_en: "Don't transfer immediately. You can help with this inquiry.",
            feedback_es: "No transfieras inmediatamente. Puedes ayudar con esta consulta."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Great! I have a small trucking company and I want to add another truck to my fleet. It's about $85,000. What do I need to get started?",
        audio_es: "¡Genial! Tengo una pequeña compañía de transporte y quiero agregar otro camión a mi flotilla. Es como $85,000 dólares. ¿Qué necesito para empezar?",
        options: [
          {
            text_en: "I'd be happy to help! For equipment financing, we'll need some basic documents: your business information, bank statements from the last 3-6 months, and details about the truck. Let me send you our application link to get started.",
            text_es: "¡Con gusto te ayudo! Para financiamiento de equipo, necesitaremos algunos documentos básicos: información de tu negocio, estados de cuenta bancarios de los últimos 3-6 meses, y detalles del camión. Déjame enviarte nuestro link de solicitud para empezar.",
            correct: true,
            feedback_en: "Perfect! You explained the requirements clearly and offered the next step.",
            feedback_es: "¡Perfecto! Explicaste los requisitos claramente y ofreciste el siguiente paso."
          },
          {
            text_en: "Just fill out the online form.",
            text_es: "Solo llena el formulario en línea.",
            correct: false,
            feedback_en: "Too brief. Customers need more guidance about requirements.",
            feedback_es: "Muy breve. Los clientes necesitan más orientación sobre los requisitos."
          },
          {
            text_en: "We need your social security number and credit score first.",
            text_es: "Necesitamos tu número de seguro social y puntaje de crédito primero.",
            correct: false,
            feedback_en: "Never ask for SSN directly on a call. Guide them to the secure application.",
            feedback_es: "Nunca pidas el SSN directamente en una llamada. Guíalos a la solicitud segura."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "That sounds good. How long does the approval process take? And what kind of down payment do you usually require?",
        audio_es: "Suena bien. ¿Cuánto tiempo toma el proceso de aprobación? ¿Y qué tipo de enganche normalmente requieren?",
        options: [
          {
            text_en: "Typically, once we have all documents, approval can take 24-48 hours. Down payment varies based on credit, usually between 5-20% of the equipment price. Would you like me to generate a quick quote using our LOI pricer so you can see potential payment options?",
            text_es: "Típicamente, una vez que tenemos todos los documentos, la aprobación puede tomar 24-48 horas. El enganche varía según el crédito, usualmente entre 5-20% del precio del equipo. ¿Te gustaría que genere una cotización rápida usando nuestro LOI pricer para que veas opciones de pago?",
            correct: true,
            feedback_en: "Excellent! You provided accurate info and proactively offered to help with pricing.",
            feedback_es: "¡Excelente! Proporcionaste información precisa y ofreciste proactivamente ayudar con los precios."
          },
          {
            text_en: "I don't know, it depends.",
            text_es: "No sé, depende.",
            correct: false,
            feedback_en: "Vague answers don't help customers. Provide general ranges even if exact varies.",
            feedback_es: "Las respuestas vagas no ayudan a los clientes. Proporciona rangos generales aunque el exacto varíe."
          },
          {
            text_en: "You'll need at least 50% down payment.",
            text_es: "Vas a necesitar al menos 50% de enganche.",
            correct: false,
            feedback_en: "Incorrect information. Down payments are typically 5-20%, not 50%.",
            feedback_es: "Información incorrecta. Los enganches típicamente son 5-20%, no 50%."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Yes, please send me the application link. My email is john at trucking company dot com.",
        audio_es: "Sí, por favor envíame el link de la solicitud. Mi correo es juan arroba compania de transporte punto com.",
        options: [
          {
            text_en: "Perfect! I'm sending you the application link right now to that email. The link is inspiredfunding.org/get-funding. Is there anything else I can help you with today?",
            text_es: "¡Perfecto! Te estoy enviando el link de solicitud ahora mismo a ese correo. El link es inspiredfunding.org/get-funding. ¿Hay algo más en lo que pueda ayudarte hoy?",
            correct: true,
            feedback_en: "Great closing! You confirmed the action and offered further assistance.",
            feedback_es: "¡Excelente cierre! Confirmaste la acción y ofreciste más asistencia."
          },
          {
            text_en: "Ok, bye.",
            text_es: "Ok, adiós.",
            correct: false,
            feedback_en: "Too abrupt. Always confirm actions and offer additional help before ending.",
            feedback_es: "Muy abrupto. Siempre confirma acciones y ofrece ayuda adicional antes de terminar."
          },
          {
            text_en: "I'll send it when I have time.",
            text_es: "Te lo envío cuando tenga tiempo.",
            correct: false,
            feedback_en: "Never delay customer requests. Send immediately or give specific timeframe.",
            feedback_es: "Nunca retrases las solicitudes de clientes. Envía inmediatamente o da un tiempo específico."
          }
        ]
      }
    ]
  },
  'angry-customer': {
    id: 'angry-customer',
    title_en: 'Handling Upset Customer',
    title_es: 'Manejando Cliente Molesto',
    description_en: 'A customer is frustrated about their application delay.',
    description_es: 'Un cliente está frustrado por el retraso de su solicitud.',
    difficulty: 'hard',
    duration: 15,
    icon: '😤',
    steps: [
      {
        type: 'customer',
        audio_en: "I've been waiting for TWO WEEKS for my application! Nobody calls me back. This is ridiculous! I need this truck for my business!",
        audio_es: "¡He estado esperando DOS SEMANAS por mi solicitud! Nadie me devuelve la llamada. ¡Esto es ridículo! ¡Necesito este camión para mi negocio!",
        options: [
          {
            text_en: "I completely understand your frustration, and I sincerely apologize for the delay. Let me pull up your application right now and find out exactly what's happening. Can you give me your name or application number?",
            text_es: "Entiendo completamente tu frustración, y me disculpo sinceramente por el retraso. Déjame buscar tu solicitud ahora mismo y averiguar exactamente qué está pasando. ¿Me puedes dar tu nombre o número de solicitud?",
            correct: true,
            feedback_en: "Perfect de-escalation. You acknowledged feelings, apologized, and took immediate action.",
            feedback_es: "Perfecta desescalada. Reconociste los sentimientos, te disculpaste y tomaste acción inmediata."
          },
          {
            text_en: "Sir, please calm down. I can't help you if you're yelling.",
            text_es: "Señor, por favor cálmese. No puedo ayudarle si está gritando.",
            correct: false,
            feedback_en: "Never tell angry customers to calm down. It escalates the situation.",
            feedback_es: "Nunca le digas a clientes enojados que se calmen. Escala la situación."
          },
          {
            text_en: "Two weeks isn't that long. These things take time.",
            text_es: "Dos semanas no es tanto tiempo. Estas cosas toman tiempo.",
            correct: false,
            feedback_en: "Dismissing concerns makes customers angrier. Always validate their feelings.",
            feedback_es: "Descartar las preocupaciones hace a los clientes más enojados. Siempre valida sus sentimientos."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "It's under Johnson Trucking. Application number JT-2024-0892. I really need to know what's going on.",
        audio_es: "Está bajo Johnson Trucking. Número de solicitud JT-2024-0892. Realmente necesito saber qué está pasando.",
        options: [
          {
            text_en: "Thank you, Mr. Johnson. I have your file right here. I can see the credit team is waiting for one additional document - your most recent bank statement. Once we receive that, we can move forward immediately. Would you like me to help you submit it right now?",
            text_es: "Gracias, Sr. Johnson. Tengo su archivo aquí. Veo que el equipo de crédito está esperando un documento adicional - su estado de cuenta bancario más reciente. Una vez que lo recibamos, podemos avanzar inmediatamente. ¿Le gustaría que le ayude a enviarlo ahora mismo?",
            correct: true,
            feedback_en: "Excellent! You found the issue, explained clearly, and offered immediate help.",
            feedback_es: "¡Excelente! Encontraste el problema, explicaste claramente y ofreciste ayuda inmediata."
          },
          {
            text_en: "I see your application. It's still pending. You just have to wait.",
            text_es: "Veo tu solicitud. Todavía está pendiente. Solo tienes que esperar.",
            correct: false,
            feedback_en: "Unhelpful. Always explain WHY something is pending and what can be done.",
            feedback_es: "No es útil. Siempre explica POR QUÉ algo está pendiente y qué se puede hacer."
          },
          {
            text_en: "I don't see anything in the system. Are you sure you applied here?",
            text_es: "No veo nada en el sistema. ¿Estás seguro que aplicaste aquí?",
            correct: false,
            feedback_en: "Never question the customer's memory. Double-check before saying you can't find it.",
            feedback_es: "Nunca cuestiones la memoria del cliente. Verifica dos veces antes de decir que no lo encuentras."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Nobody told me I needed another bank statement! I thought I sent everything already. This is so frustrating...",
        audio_es: "¡Nadie me dijo que necesitaba otro estado de cuenta bancario! Pensé que ya había enviado todo. Esto es tan frustrante...",
        options: [
          {
            text_en: "I completely understand, and I apologize for any miscommunication. To make this as easy as possible, I can stay on the line while you email it, or if you prefer, you can take a photo and upload it directly to your application portal. Which would work better for you?",
            text_es: "Entiendo completamente, y me disculpo por cualquier falta de comunicación. Para hacerlo lo más fácil posible, puedo quedarme en la línea mientras lo envías por email, o si prefieres, puedes tomar una foto y subirla directamente al portal de tu solicitud. ¿Cuál te funciona mejor?",
            correct: true,
            feedback_en: "Great recovery! You apologized, offered multiple solutions, and gave the customer control.",
            feedback_es: "¡Gran recuperación! Te disculpaste, ofreciste múltiples soluciones y diste control al cliente."
          },
          {
            text_en: "It's in the email we sent you. You should read your emails more carefully.",
            text_es: "Está en el email que te enviamos. Deberías leer tus emails con más cuidado.",
            correct: false,
            feedback_en: "Never blame the customer. Focus on solutions, not fault.",
            feedback_es: "Nunca culpes al cliente. Enfócate en soluciones, no en culpa."
          },
          {
            text_en: "Just upload it to the portal when you get a chance.",
            text_es: "Solo súbelo al portal cuando puedas.",
            correct: false,
            feedback_en: "Too passive. Offer to help them complete it NOW while you have them.",
            feedback_es: "Muy pasivo. Ofrece ayudarles a completarlo AHORA mientras los tienes."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Okay... I can email it right now. And after that, how quickly will I get an answer?",
        audio_es: "Okay... puedo enviarlo por email ahora mismo. ¿Y después de eso, qué tan rápido tendré una respuesta?",
        options: [
          {
            text_en: "Once we receive it, I'll personally make sure it gets to the credit team today. You should hear back within 24-48 hours. I'm also going to add a note to your file about this situation so everyone is aware. Here's my direct extension if you have any other questions.",
            text_es: "Una vez que lo recibamos, personalmente me aseguraré de que llegue al equipo de crédito hoy. Deberías tener respuesta en 24-48 horas. También voy a agregar una nota a tu archivo sobre esta situación para que todos estén al tanto. Aquí está mi extensión directa si tienes otras preguntas.",
            correct: true,
            feedback_en: "Perfect finish! You set expectations, took ownership, and provided direct contact.",
            feedback_es: "¡Final perfecto! Estableciste expectativas, tomaste responsabilidad y diste contacto directo."
          },
          {
            text_en: "Hard to say. Could be a few days, could be longer.",
            text_es: "Difícil decir. Podrían ser unos días, podría ser más.",
            correct: false,
            feedback_en: "Always give specific timeframes when possible to manage expectations.",
            feedback_es: "Siempre da plazos específicos cuando sea posible para manejar expectativas."
          },
          {
            text_en: "As soon as possible. Goodbye.",
            text_es: "Lo más pronto posible. Adiós.",
            correct: false,
            feedback_en: "Abrupt ending. After a difficult call, ensure the customer feels supported.",
            feedback_es: "Final abrupto. Después de una llamada difícil, asegúrate que el cliente se sienta apoyado."
          }
        ]
      }
    ]
  },
  'ban-list': {
    id: 'ban-list',
    title_en: 'Ban List Verification',
    title_es: 'Verificación de Lista de Prohibidos',
    description_en: 'Verify a customer is not on the ban list before processing.',
    description_es: 'Verifica que un cliente no esté en la lista de prohibidos antes de procesar.',
    difficulty: 'easy',
    duration: 5,
    icon: '🚫',
    steps: [
      {
        type: 'customer',
        audio_en: "Hi, I want to apply for truck financing. My name is Robert Martinez and my company is Fast Freight LLC.",
        audio_es: "Hola, quiero aplicar para financiamiento de camión. Mi nombre es Roberto Martínez y mi compañía es Fast Freight LLC.",
        options: [
          {
            text_en: "Thank you, Mr. Martinez. Before we proceed, I need to verify some information in our system. Can you please hold for just a moment?",
            text_es: "Gracias, Sr. Martínez. Antes de proceder, necesito verificar alguna información en nuestro sistema. ¿Puede esperar un momento por favor?",
            correct: true,
            feedback_en: "Good! Always verify the ban list before proceeding with any application.",
            feedback_es: "¡Bien! Siempre verifica la lista de prohibidos antes de proceder con cualquier solicitud."
          },
          {
            text_en: "Great! Let me send you the application link right away.",
            text_es: "¡Genial! Déjame enviarte el link de solicitud de inmediato.",
            correct: false,
            feedback_en: "WRONG! You must ALWAYS check the ban list BEFORE processing any application.",
            feedback_es: "¡INCORRECTO! SIEMPRE debes verificar la lista de prohibidos ANTES de procesar cualquier solicitud."
          },
          {
            text_en: "Are you on our ban list?",
            text_es: "¿Estás en nuestra lista de prohibidos?",
            correct: false,
            feedback_en: "Never ask the customer directly. Check the list yourself discreetly.",
            feedback_es: "Nunca preguntes al cliente directamente. Verifica la lista tú mismo discretamente."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 SYSTEM: You check the ban list and find 'Fast Freight LLC' is listed as BANNED due to 'Fraud - Document Falsification'. What do you do?",
        message_es: "📋 SISTEMA: Verificas la lista de prohibidos y encuentras que 'Fast Freight LLC' está listada como PROHIBIDA por 'Fraude - Falsificación de Documentos'. ¿Qué haces?",
        options: [
          {
            text_en: "I apologize, Mr. Martinez, but I'm unable to proceed with your application at this time. I recommend reaching out to our main office for more information.",
            text_es: "Lo siento, Sr. Martínez, pero no puedo proceder con su solicitud en este momento. Le recomiendo contactar nuestra oficina principal para más información.",
            correct: true,
            feedback_en: "Correct! Professional decline without revealing the ban list details.",
            feedback_es: "¡Correcto! Declinaste profesionalmente sin revelar detalles de la lista de prohibidos."
          },
          {
            text_en: "Sir, you're on our ban list for fraud. We can't help you.",
            text_es: "Señor, usted está en nuestra lista de prohibidos por fraude. No podemos ayudarle.",
            correct: false,
            feedback_en: "Never reveal ban list details to the customer. Keep it professional and vague.",
            feedback_es: "Nunca reveles detalles de la lista de prohibidos al cliente. Mantenlo profesional y vago."
          },
          {
            text_en: "I'll process your application anyway since you seem nice.",
            text_es: "Voy a procesar tu solicitud de todos modos ya que pareces buena persona.",
            correct: false,
            feedback_en: "NEVER process banned customers! This could result in termination.",
            feedback_es: "¡NUNCA proceses clientes prohibidos! Esto podría resultar en despido."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "What? Why can't you help me? I've never applied here before!",
        audio_es: "¿Qué? ¿Por qué no pueden ayudarme? ¡Nunca he aplicado aquí antes!",
        options: [
          {
            text_en: "I understand this is frustrating. Unfortunately, I'm not able to provide specific details, but I encourage you to contact our main office at your convenience. They would be better able to assist you with any questions.",
            text_es: "Entiendo que esto es frustrante. Desafortunadamente, no puedo proporcionar detalles específicos, pero le animo a contactar nuestra oficina principal cuando guste. Ellos podrían ayudarle mejor con cualquier pregunta.",
            correct: true,
            feedback_en: "Perfect handling. Stayed professional without revealing sensitive information.",
            feedback_es: "Manejo perfecto. Te mantuviste profesional sin revelar información sensible."
          },
          {
            text_en: "Your company committed fraud. That's why you're banned.",
            text_es: "Tu compañía cometió fraude. Por eso están prohibidos.",
            correct: false,
            feedback_en: "Never reveal ban reasons. This could create legal issues.",
            feedback_es: "Nunca reveles razones del ban. Esto podría crear problemas legales."
          },
          {
            text_en: "I don't know, that's just what the system says. Sorry.",
            text_es: "No sé, eso es lo que dice el sistema. Lo siento.",
            correct: false,
            feedback_en: "Unhelpful and unprofessional. Always provide a proper direction for the customer.",
            feedback_es: "No es útil y es poco profesional. Siempre proporciona una dirección apropiada para el cliente."
          }
        ]
      }
    ]
  },
  'loi-pricing': {
    id: 'loi-pricing',
    title_en: 'Generate LOI Quote',
    title_es: 'Generar Cotización LOI',
    description_en: 'Customer wants pricing options before committing.',
    description_es: 'El cliente quiere opciones de precio antes de comprometerse.',
    difficulty: 'medium',
    duration: 10,
    icon: '💰',
    steps: [
      {
        type: 'customer',
        audio_en: "Hi, I'm interested in financing a 2022 Freightliner Cascadia. The dealer is asking $125,000. Before I commit, I want to know what my monthly payments would look like.",
        audio_es: "Hola, estoy interesado en financiar un Freightliner Cascadia 2022. El concesionario pide $125,000 dólares. Antes de comprometerme, quiero saber cómo serían mis pagos mensuales.",
        options: [
          {
            text_en: "Absolutely! I can help you with that. Let me use our LOI pricer to generate some payment options for you. To give you accurate estimates, I'll need to know: what down payment are you thinking, and do you prefer a 5, 6, or 7-year term?",
            text_es: "¡Por supuesto! Puedo ayudarte con eso. Déjame usar nuestro LOI pricer para generarte algunas opciones de pago. Para darte estimados precisos, necesito saber: ¿qué enganche estás pensando, y prefieres plazo de 5, 6 o 7 años?",
            correct: true,
            feedback_en: "Excellent! You offered to help and asked the right qualifying questions.",
            feedback_es: "¡Excelente! Ofreciste ayuda y hiciste las preguntas correctas de calificación."
          },
          {
            text_en: "You need to apply first before I can give you any numbers.",
            text_es: "Necesitas aplicar primero antes de que pueda darte cualquier número.",
            correct: false,
            feedback_en: "The LOI pricer is specifically for quotes BEFORE full application. Use it!",
            feedback_es: "El LOI pricer es específicamente para cotizaciones ANTES de la solicitud completa. ¡Úsalo!"
          },
          {
            text_en: "Probably around $2,500 a month. That's pretty standard.",
            text_es: "Probablemente alrededor de $2,500 al mes. Eso es bastante estándar.",
            correct: false,
            feedback_en: "Never guess at payments. Always use the LOI pricer for accurate quotes.",
            feedback_es: "Nunca adivines los pagos. Siempre usa el LOI pricer para cotizaciones precisas."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "I was thinking about $15,000 down. And probably 6 years would work best for my cash flow.",
        audio_es: "Estaba pensando en $15,000 de enganche. Y probablemente 6 años funcionaría mejor para mi flujo de caja.",
        options: [
          {
            text_en: "Perfect. So that's $125,000 equipment price, $15,000 down, 72-month term. Let me run those numbers... Based on typical rates, you're looking at approximately $1,800-$2,100 per month depending on credit approval. Would you like me to email you a formal LOI with exact figures?",
            text_es: "Perfecto. Entonces es $125,000 precio del equipo, $15,000 de enganche, plazo de 72 meses. Déjame calcular esos números... Basándome en tasas típicas, estarías viendo aproximadamente $1,800-$2,100 al mes dependiendo de la aprobación de crédito. ¿Te gustaría que te envíe un LOI formal con cifras exactas?",
            correct: true,
            feedback_en: "Great! You summarized the details, gave a range, and offered the next step.",
            feedback_es: "¡Genial! Resumiste los detalles, diste un rango y ofreciste el siguiente paso."
          },
          {
            text_en: "That will be exactly $1,950 per month.",
            text_es: "Eso será exactamente $1,950 al mes.",
            correct: false,
            feedback_en: "Never give exact figures without credit approval. Always give ranges.",
            feedback_es: "Nunca des cifras exactas sin aprobación de crédito. Siempre da rangos."
          },
          {
            text_en: "I'm not sure how to use the LOI pricer. Let me ask my supervisor.",
            text_es: "No estoy seguro de cómo usar el LOI pricer. Déjame preguntarle a mi supervisor.",
            correct: false,
            feedback_en: "You should know your tools. Practice using the LOI pricer in the sandbox.",
            feedback_es: "Debes conocer tus herramientas. Practica usando el LOI pricer en el sandbox."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Yes, please email me the LOI. My email is mike at expresstrucker dot com. And what would happen if I wanted to put more down?",
        audio_es: "Sí, por favor envíame el LOI por email. Mi correo es mike arroba expresstrucker punto com. ¿Y qué pasaría si quisiera poner más de enganche?",
        options: [
          {
            text_en: "Great question! A larger down payment would lower your monthly payment and could also help with credit approval. For example, with $25,000 down, you might see payments closer to $1,600-$1,850. I'll include both scenarios in the LOI I'm sending you.",
            text_es: "¡Excelente pregunta! Un enganche mayor reduciría tu pago mensual y también podría ayudar con la aprobación de crédito. Por ejemplo, con $25,000 de enganche, podrías ver pagos más cerca de $1,600-$1,850. Incluiré ambos escenarios en el LOI que te envío.",
            correct: true,
            feedback_en: "Excellent! You explained the benefit and offered to show comparison options.",
            feedback_es: "¡Excelente! Explicaste el beneficio y ofreciste mostrar opciones de comparación."
          },
          {
            text_en: "It would be lower. I don't know exactly how much.",
            text_es: "Sería más bajo. No sé exactamente cuánto.",
            correct: false,
            feedback_en: "Use the LOI pricer to give specific estimates. That's its purpose.",
            feedback_es: "Usa el LOI pricer para dar estimados específicos. Ese es su propósito."
          },
          {
            text_en: "Just pick whatever down payment you want and apply.",
            text_es: "Solo escoge el enganche que quieras y aplica.",
            correct: false,
            feedback_en: "Unhelpful. Customers want guidance to make informed decisions.",
            feedback_es: "No es útil. Los clientes quieren orientación para tomar decisiones informadas."
          }
        ]
      }
    ]
  },
  'document-collection': {
    id: 'document-collection',
    title_en: 'Document Collection',
    title_es: 'Recolección de Documentos',
    description_en: 'Help a customer gather required documents.',
    description_es: 'Ayuda a un cliente a reunir los documentos requeridos.',
    difficulty: 'medium',
    duration: 12,
    icon: '📄',
    steps: [
      {
        type: 'customer',
        audio_en: "Hi, I submitted my application last week and received an email saying I need more documents. I'm not sure what else you need.",
        audio_es: "Hola, envié mi solicitud la semana pasada y recibí un email diciendo que necesitan más documentos. No estoy seguro de qué más necesitan.",
        options: [
          {
            text_en: "I'd be happy to help clarify that. Let me pull up your file. Can you give me your name or application number so I can see exactly what documents are still needed?",
            text_es: "Con gusto te ayudo a aclarar eso. Déjame buscar tu archivo. ¿Me puedes dar tu nombre o número de solicitud para ver exactamente qué documentos todavía se necesitan?",
            correct: true,
            feedback_en: "Good approach! You offered to help and asked for identifying information.",
            feedback_es: "¡Buen enfoque! Ofreciste ayuda y pediste información de identificación."
          },
          {
            text_en: "Did you read the email? It should tell you everything.",
            text_es: "¿Leíste el email? Debería decirte todo.",
            correct: false,
            feedback_en: "Unhelpful and dismissive. Always assist customers even if info was provided.",
            feedback_es: "No es útil y es despectivo. Siempre asiste a los clientes aunque la info se haya proporcionado."
          },
          {
            text_en: "We probably need bank statements, tax returns, and a voided check.",
            text_es: "Probablemente necesitamos estados de cuenta, declaraciones de impuestos y un cheque anulado.",
            correct: false,
            feedback_en: "Don't guess. Look up their specific file to see what's actually needed.",
            feedback_es: "No adivines. Busca su archivo específico para ver qué se necesita realmente."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "It's Maria Santos. Application number MS-2024-1156.",
        audio_es: "Es María Santos. Número de solicitud MS-2024-1156.",
        options: [
          {
            text_en: "Thank you, Ms. Santos. I see your file here. We need three more items: your most recent bank statement showing December activity, a copy of your commercial driver's license, and proof of insurance. Do you have access to these documents now?",
            text_es: "Gracias, Sra. Santos. Veo tu archivo aquí. Necesitamos tres cosas más: tu estado de cuenta bancario más reciente mostrando actividad de diciembre, una copia de tu licencia de conducir comercial, y comprobante de seguro. ¿Tienes acceso a estos documentos ahora?",
            correct: true,
            feedback_en: "Perfect! You specifically identified what's needed and checked availability.",
            feedback_es: "¡Perfecto! Identificaste específicamente lo que se necesita y verificaste disponibilidad."
          },
          {
            text_en: "You're missing documents. Please upload them to the portal.",
            text_es: "Te faltan documentos. Por favor súbelos al portal.",
            correct: false,
            feedback_en: "Too vague. Always specify EXACTLY which documents are needed.",
            feedback_es: "Muy vago. Siempre especifica EXACTAMENTE qué documentos se necesitan."
          },
          {
            text_en: "I can't find your application. Are you sure you applied with us?",
            text_es: "No puedo encontrar tu solicitud. ¿Estás segura que aplicaste con nosotros?",
            correct: false,
            feedback_en: "Double-check before saying you can't find something. Try different searches.",
            feedback_es: "Verifica dos veces antes de decir que no encuentras algo. Intenta diferentes búsquedas."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "I have the bank statement and my CDL, but I'm still waiting for my insurance certificate. Can I send what I have now and the insurance later?",
        audio_es: "Tengo el estado de cuenta bancario y mi CDL, pero todavía estoy esperando mi certificado de seguro. ¿Puedo enviar lo que tengo ahora y el seguro después?",
        options: [
          {
            text_en: "Absolutely! Go ahead and send what you have now - that will help move things along. For the insurance certificate, just send it as soon as you receive it. Would you like to email them to me directly, or upload to the portal?",
            text_es: "¡Por supuesto! Envía lo que tienes ahora - eso ayudará a avanzar las cosas. Para el certificado de seguro, solo envíalo en cuanto lo recibas. ¿Te gustaría enviármelos directamente por email, o subirlos al portal?",
            correct: true,
            feedback_en: "Great flexibility! Accepting partial documents helps keep things moving.",
            feedback_es: "¡Gran flexibilidad! Aceptar documentos parciales ayuda a mantener las cosas en movimiento."
          },
          {
            text_en: "No, we need everything at once. Don't send anything until you have it all.",
            text_es: "No, necesitamos todo a la vez. No envíes nada hasta que lo tengas todo.",
            correct: false,
            feedback_en: "Incorrect. Partial submissions are fine and help speed up processing.",
            feedback_es: "Incorrecto. Los envíos parciales están bien y ayudan a acelerar el procesamiento."
          },
          {
            text_en: "How long until you get the insurance? We can't wait forever.",
            text_es: "¿Cuánto tiempo hasta que obtengas el seguro? No podemos esperar para siempre.",
            correct: false,
            feedback_en: "Pressuring customers is counterproductive. Be patient and helpful.",
            feedback_es: "Presionar a los clientes es contraproducente. Sé paciente y servicial."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "I'll email them to you. What's the email address I should send them to?",
        audio_es: "Te los enviaré por email. ¿Cuál es la dirección de email a la que debo enviarlos?",
        options: [
          {
            text_en: "You can send them to documents@inspiredfunding.org. Please include your application number MS-2024-1156 in the subject line so we can match them to your file quickly. I'll watch for them and make sure they get uploaded right away.",
            text_es: "Puedes enviarlos a documents@inspiredfunding.org. Por favor incluye tu número de solicitud MS-2024-1156 en el asunto para que podamos emparejarlos con tu archivo rápidamente. Estaré pendiente de ellos y me aseguraré de que se suban de inmediato.",
            correct: true,
            feedback_en: "Perfect! You gave clear instructions and took personal ownership.",
            feedback_es: "¡Perfecto! Diste instrucciones claras y tomaste responsabilidad personal."
          },
          {
            text_en: "Just find it on our website.",
            text_es: "Solo encuéntralo en nuestro sitio web.",
            correct: false,
            feedback_en: "Unhelpful. Always provide specific information when asked.",
            feedback_es: "No es útil. Siempre proporciona información específica cuando te preguntan."
          },
          {
            text_en: "Send it to my personal email, I'll forward it.",
            text_es: "Envíalo a mi email personal, yo lo reenvío.",
            correct: false,
            feedback_en: "Never use personal email for business documents. Security risk!",
            feedback_es: "Nunca uses email personal para documentos de negocio. ¡Riesgo de seguridad!"
          }
        ]
      }
    ]
  },
  'crm-documentation': {
    id: 'crm-documentation',
    title_en: 'CRM Documentation',
    title_es: 'Documentación en CRM',
    description_en: 'Properly document a customer interaction in 360Portal.',
    description_es: 'Documenta apropiadamente una interacción con cliente en 360Portal.',
    difficulty: 'easy',
    duration: 8,
    icon: '📝',
    steps: [
      {
        type: 'system',
        message_en: "📋 SCENARIO: You just finished a call with Carlos Mendez about his application status. He's waiting for credit approval and asked about down payment options. What should you document in 360Portal.com?",
        message_es: "📋 ESCENARIO: Acabas de terminar una llamada con Carlos Méndez sobre el estado de su solicitud. Está esperando aprobación de crédito y preguntó sobre opciones de enganche. ¿Qué debes documentar en 360Portal.com?",
        options: [
          {
            text_en: "Log a call note with: date/time, spoke with Carlos Mendez, discussed application status (pending credit approval), explained down payment options of 10-20%, customer will wait for approval decision, follow up in 48 hours",
            text_es: "Registrar nota de llamada con: fecha/hora, hablé con Carlos Méndez, discutimos estado de solicitud (pendiente aprobación crédito), expliqué opciones de enganche de 10-20%, cliente esperará decisión de aprobación, seguimiento en 48 horas",
            correct: true,
            feedback_en: "Excellent documentation! Includes all key details and next steps.",
            feedback_es: "¡Excelente documentación! Incluye todos los detalles clave y próximos pasos."
          },
          {
            text_en: "Just write 'Called customer'",
            text_es: "Solo escribir 'Llamé al cliente'",
            correct: false,
            feedback_en: "Way too brief. Anyone reading this later won't know what was discussed.",
            feedback_es: "Muy breve. Cualquiera que lea esto después no sabrá qué se discutió."
          },
          {
            text_en: "Don't document - it was just a quick status call",
            text_es: "No documentar - fue solo una llamada rápida de estado",
            correct: false,
            feedback_en: "EVERY customer interaction should be documented. No exceptions.",
            feedback_es: "CADA interacción con cliente debe documentarse. Sin excepciones."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 SCENARIO: During the call, Carlos mentioned he might need a larger truck than originally planned. How should you update the CRM deal?",
        message_es: "📋 ESCENARIO: Durante la llamada, Carlos mencionó que podría necesitar un camión más grande de lo que originalmente planeó. ¿Cómo debes actualizar el trato en el CRM?",
        options: [
          {
            text_en: "Add a note about the potential equipment change, update the deal value if known, and flag for credit team review since loan amount may increase",
            text_es: "Agregar una nota sobre el posible cambio de equipo, actualizar el valor del trato si se conoce, y marcar para revisión del equipo de crédito ya que el monto del préstamo puede aumentar",
            correct: true,
            feedback_en: "Perfect! You captured the change and flagged it for appropriate team.",
            feedback_es: "¡Perfecto! Capturaste el cambio y lo marcaste para el equipo apropiado."
          },
          {
            text_en: "Wait until he confirms before updating anything",
            text_es: "Esperar hasta que confirme antes de actualizar algo",
            correct: false,
            feedback_en: "Document potential changes immediately. You can update again when confirmed.",
            feedback_es: "Documenta cambios potenciales inmediatamente. Puedes actualizar de nuevo cuando se confirme."
          },
          {
            text_en: "Create a whole new deal for the bigger truck",
            text_es: "Crear un trato completamente nuevo para el camión más grande",
            correct: false,
            feedback_en: "Don't create duplicates. Update the existing deal with new information.",
            feedback_es: "No crees duplicados. Actualiza el trato existente con nueva información."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 SCENARIO: What deal stage should this customer be in based on what you know?",
        message_es: "📋 ESCENARIO: ¿En qué etapa del trato debería estar este cliente basándote en lo que sabes?",
        options: [
          {
            text_en: "Credit Review / Pending Approval - since application is submitted and awaiting credit decision",
            text_es: "Revisión de Crédito / Pendiente de Aprobación - ya que la solicitud está enviada y esperando decisión de crédito",
            correct: true,
            feedback_en: "Correct stage! Always keep deal stages accurate for proper tracking.",
            feedback_es: "¡Etapa correcta! Siempre mantén las etapas del trato precisas para seguimiento apropiado."
          },
          {
            text_en: "New Lead - we're still talking to them",
            text_es: "Lead Nuevo - todavía estamos hablando con ellos",
            correct: false,
            feedback_en: "Incorrect. They've already applied, so they're past the 'New Lead' stage.",
            feedback_es: "Incorrecto. Ya aplicaron, así que pasaron la etapa de 'Lead Nuevo'."
          },
          {
            text_en: "Closed Won - they want to buy",
            text_es: "Cerrado Ganado - quieren comprar",
            correct: false,
            feedback_en: "Too early! 'Closed Won' is only after funding is complete.",
            feedback_es: "¡Muy temprano! 'Cerrado Ganado' es solo después de que el financiamiento esté completo."
          }
        ]
      }
    ]
  },
  'credit-denial': {
    id: 'credit-denial',
    title_en: 'Credit Denial Handling',
    title_es: 'Manejo de Negación de Crédito',
    description_en: 'A customer calls after receiving a credit denial. Handle professionally.',
    description_es: 'Un cliente llama después de recibir una negación de crédito. Maneja profesionalmente.',
    difficulty: 'hard',
    duration: 15,
    icon: '❌',
    steps: [
      {
        type: 'customer',
        audio_en: "I just got an email saying my application was DENIED! I've been in business for 8 years! This is outrageous! How can you do this to me?",
        audio_es: "¡Acabo de recibir un email diciendo que mi solicitud fue NEGADA! ¡Llevo 8 años en el negocio! ¡Esto es indignante! ¿Cómo me pueden hacer esto?",
        options: [
          {
            text_en: "I understand this is disappointing news, and I'm sorry you're going through this. Let me look at your file right now to understand the decision and see what options we might have. Can you give me your application number?",
            text_es: "Entiendo que esta es una noticia decepcionante, y lamento que estés pasando por esto. Déjame revisar tu archivo ahora mismo para entender la decisión y ver qué opciones podríamos tener. ¿Me puedes dar tu número de solicitud?",
            correct: true,
            feedback_en: "Excellent! You acknowledged emotions, apologized, and moved to action without making promises.",
            feedback_es: "¡Excelente! Reconociste las emociones, te disculpaste y pasaste a la acción sin hacer promesas."
          },
          {
            text_en: "If you were denied, it means your credit wasn't good enough. There's nothing I can do.",
            text_es: "Si te negaron, significa que tu crédito no era suficiente. No hay nada que pueda hacer.",
            correct: false,
            feedback_en: "Never be dismissive. There may be options to explore or ways to help in the future.",
            feedback_es: "Nunca seas despectivo. Puede haber opciones que explorar o formas de ayudar en el futuro."
          },
          {
            text_en: "Let me transfer you to a manager who can reverse this decision.",
            text_es: "Déjame transferirte a un gerente que pueda revertir esta decisión.",
            correct: false,
            feedback_en: "Don't make promises about reversing decisions. Investigate first before escalating.",
            feedback_es: "No hagas promesas sobre revertir decisiones. Investiga primero antes de escalar."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 SYSTEM: You review the file. Denial reason: Low business bank balance average ($2,400 monthly avg, requirement is $5,000+) and recent overdrafts (3 in last 60 days). What's your approach?",
        message_es: "📋 SISTEMA: Revisas el archivo. Razón de negación: Saldo bancario comercial promedio bajo ($2,400 promedio mensual, requisito es $5,000+) y sobregiros recientes (3 en últimos 60 días). ¿Cuál es tu enfoque?",
        options: [
          {
            text_en: "Mr. Customer, I've reviewed your file. The primary concerns were related to bank balance averages and account history. While I can't change this decision, I want to help you understand what would strengthen a future application. Would that be helpful?",
            text_es: "Sr. Cliente, he revisado su archivo. Las preocupaciones principales estaban relacionadas con los promedios de saldo bancario y el historial de cuenta. Aunque no puedo cambiar esta decisión, quiero ayudarle a entender qué fortalecería una solicitud futura. ¿Le sería útil?",
            correct: true,
            feedback_en: "Perfect! You explained without revealing exact details and offered constructive help.",
            feedback_es: "¡Perfecto! Explicaste sin revelar detalles exactos y ofreciste ayuda constructiva."
          },
          {
            text_en: "Your bank account only had $2,400 average and you had 3 overdrafts. That's why you were denied.",
            text_es: "Tu cuenta bancaria solo tenía $2,400 de promedio y tuviste 3 sobregiros. Por eso te negaron.",
            correct: false,
            feedback_en: "Never reveal exact figures from internal analysis. Keep explanations general.",
            feedback_es: "Nunca reveles cifras exactas del análisis interno. Mantén las explicaciones generales."
          },
          {
            text_en: "I can't tell you why you were denied. It's confidential.",
            text_es: "No puedo decirte por qué te negaron. Es confidencial.",
            correct: false,
            feedback_en: "You can give general guidance without revealing exact internal metrics.",
            feedback_es: "Puedes dar orientación general sin revelar métricas internas exactas."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "So what am I supposed to do now? I NEED this truck to grow my business! Can I reapply with a bigger down payment or something?",
        audio_es: "¿Entonces qué se supone que haga ahora? ¡NECESITO este camión para hacer crecer mi negocio! ¿Puedo volver a aplicar con un enganche más grande o algo?",
        options: [
          {
            text_en: "A larger down payment could help, but the key factors are usually bank balances and payment history. I'd recommend: maintain higher bank balances for 3-4 months, avoid any overdrafts, and you can reapply after 90 days. Alternatively, we have lenders who work with different criteria - would you like me to explore those options?",
            text_es: "Un enganche mayor podría ayudar, pero los factores clave usualmente son los saldos bancarios y el historial de pagos. Te recomendaría: mantener saldos bancarios más altos por 3-4 meses, evitar cualquier sobregiro, y puedes volver a aplicar después de 90 días. Alternativamente, tenemos prestamistas que trabajan con diferentes criterios - ¿te gustaría que explore esas opciones?",
            correct: true,
            feedback_en: "Excellent! You gave actionable advice and offered alternative solutions.",
            feedback_es: "¡Excelente! Diste consejos accionables y ofreciste soluciones alternativas."
          },
          {
            text_en: "Yes, just reapply with more money down and you'll be approved.",
            text_es: "Sí, solo vuelve a aplicar con más dinero de enganche y serás aprobado.",
            correct: false,
            feedback_en: "Never guarantee approval. Down payment alone may not resolve all issues.",
            feedback_es: "Nunca garantices aprobación. El enganche solo puede no resolver todos los problemas."
          },
          {
            text_en: "I'm sorry, there's nothing we can do. You'll need to go somewhere else.",
            text_es: "Lo siento, no hay nada que podamos hacer. Necesitarás ir a otro lugar.",
            correct: false,
            feedback_en: "Always offer paths forward or alternatives. Don't close the door completely.",
            feedback_es: "Siempre ofrece caminos hacia adelante o alternativas. No cierres la puerta completamente."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "What if I get a co-signer? My brother has excellent credit and owns his own trucking company.",
        audio_es: "¿Qué tal si consigo un co-firmante? Mi hermano tiene excelente crédito y tiene su propia compañía de transporte.",
        options: [
          {
            text_en: "A co-signer with strong credit could definitely help! However, they would need to understand they're equally responsible for the loan. If your brother is willing, have him call us or apply together at inspiredfunding.org/get-funding. I'll add a note to your file about this option.",
            text_es: "¡Un co-firmante con buen crédito definitivamente podría ayudar! Sin embargo, necesitaría entender que es igualmente responsable del préstamo. Si tu hermano está dispuesto, que nos llame o apliquen juntos en inspiredfunding.org/get-funding. Agregaré una nota a tu archivo sobre esta opción.",
            correct: true,
            feedback_en: "Perfect! You explained the co-signer's responsibility and provided clear next steps.",
            feedback_es: "¡Perfecto! Explicaste la responsabilidad del co-firmante y diste pasos claros a seguir."
          },
          {
            text_en: "Yes, get a co-signer and you're guaranteed approval.",
            text_es: "Sí, consigue un co-firmante y tienes aprobación garantizada.",
            correct: false,
            feedback_en: "Never guarantee approval. Co-signer helps but doesn't guarantee anything.",
            feedback_es: "Nunca garantices aprobación. El co-firmante ayuda pero no garantiza nada."
          },
          {
            text_en: "We don't do co-signers on our applications.",
            text_es: "No aceptamos co-firmantes en nuestras solicitudes.",
            correct: false,
            feedback_en: "Incorrect. Co-signers are an option for strengthening applications.",
            feedback_es: "Incorrecto. Los co-firmantes son una opción para fortalecer solicitudes."
          }
        ]
      }
    ]
  },
  'multi-unit-deal': {
    id: 'multi-unit-deal',
    title_en: 'Complex Multi-Unit Deal',
    title_es: 'Trato Complejo de Múltiples Unidades',
    description_en: 'Handle a large fleet financing request with multiple complications.',
    description_es: 'Maneja una solicitud grande de financiamiento de flotilla con múltiples complicaciones.',
    difficulty: 'hard',
    duration: 20,
    icon: '🚛🚛🚛',
    steps: [
      {
        type: 'customer',
        audio_en: "Hi, I'm looking to finance 5 trucks for my expanding business. We're talking about $600,000 total. I have 3 trucks I want to trade in, and I need everything ready in 2 weeks because we have contracts starting.",
        audio_es: "Hola, estoy buscando financiar 5 camiones para mi negocio en expansión. Estamos hablando de $600,000 en total. Tengo 3 camiones que quiero dar en trade-in, y necesito todo listo en 2 semanas porque tenemos contratos empezando.",
        options: [
          {
            text_en: "That's an exciting growth opportunity! Given the size and complexity of this deal, I want to make sure we set proper expectations. A $600K multi-unit deal with trade-ins will require thorough documentation. Let me ask some questions to understand your situation better. First, is this an existing business with financials we can review?",
            text_es: "¡Esa es una emocionante oportunidad de crecimiento! Dado el tamaño y complejidad de este trato, quiero asegurarme de establecer expectativas apropiadas. Un trato de múltiples unidades de $600K con trade-ins requerirá documentación exhaustiva. Déjame hacerte algunas preguntas para entender mejor tu situación. Primero, ¿es este un negocio existente con estados financieros que podamos revisar?",
            correct: true,
            feedback_en: "Perfect! You showed enthusiasm but set expectations and started qualifying.",
            feedback_es: "¡Perfecto! Mostraste entusiasmo pero estableciste expectativas y empezaste a calificar."
          },
          {
            text_en: "Sure, no problem! Let me send you the application link and we'll have you approved in no time.",
            text_es: "¡Claro, no hay problema! Déjame enviarte el link de solicitud y te aprobaremos en poco tiempo.",
            correct: false,
            feedback_en: "A $600K deal needs proper qualification. Don't oversimplify or make timeline promises.",
            feedback_es: "Un trato de $600K necesita calificación apropiada. No simplifiques demasiado ni prometas plazos."
          },
          {
            text_en: "That's too complicated. You should call our commercial department.",
            text_es: "Eso es muy complicado. Deberías llamar a nuestro departamento comercial.",
            correct: false,
            feedback_en: "You should handle this! Large deals are opportunities. Qualify and escalate if needed.",
            feedback_es: "¡Deberías manejar esto! Los tratos grandes son oportunidades. Califica y escala si es necesario."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Yes, we've been operating for 6 years. Revenues around $2 million last year. The trucks I'm trading in are a 2018 Freightliner, a 2019 Kenworth, and a 2020 Peterbilt. They're all paid off.",
        audio_es: "Sí, llevamos operando 6 años. Ingresos de alrededor de $2 millones el año pasado. Los camiones que quiero dar en trade-in son un Freightliner 2018, un Kenworth 2019 y un Peterbilt 2020. Todos están pagados.",
        options: [
          {
            text_en: "Excellent background. For a deal this size, we'll need: 3 years of business tax returns, 6 months of bank statements, current equipment list with VINs for the trade-ins, and information on the new trucks. The trade-in values will need to be appraised. Have you gotten quotes on what the trade-ins are worth?",
            text_es: "Excelente historial. Para un trato de este tamaño, necesitaremos: 3 años de declaraciones de impuestos del negocio, 6 meses de estados de cuenta bancarios, lista de equipo actual con VINs para los trade-ins, e información sobre los camiones nuevos. Los valores de trade-in necesitarán ser evaluados. ¿Has obtenido cotizaciones de cuánto valen los trade-ins?",
            correct: true,
            feedback_en: "Great! You outlined comprehensive requirements for a complex deal.",
            feedback_es: "¡Genial! Describiste requisitos completos para un trato complejo."
          },
          {
            text_en: "Just send us whatever documents you have and we'll figure it out.",
            text_es: "Solo envíanos los documentos que tengas y lo resolveremos.",
            correct: false,
            feedback_en: "Be specific about requirements. Vague requests cause delays.",
            feedback_es: "Sé específico sobre los requisitos. Las solicitudes vagas causan retrasos."
          },
          {
            text_en: "We don't do trade-ins. You'll have to sell those trucks separately.",
            text_es: "No hacemos trade-ins. Tendrás que vender esos camiones por separado.",
            correct: false,
            feedback_en: "Incorrect. Trade-ins can be factored into deals to reduce loan amounts.",
            feedback_es: "Incorrecto. Los trade-ins pueden incluirse en tratos para reducir montos de préstamo."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "I think the trade-ins are worth about $180,000 total based on what I've seen online. So really I only need to finance $420,000. Can we make the 2-week timeline work?",
        audio_es: "Creo que los trade-ins valen como $180,000 en total basado en lo que he visto en línea. Entonces realmente solo necesito financiar $420,000. ¿Podemos cumplir con el plazo de 2 semanas?",
        options: [
          {
            text_en: "I appreciate the urgency. Here's my honest assessment: getting a $420K deal with trade-ins approved in 2 weeks is ambitious but possible IF we receive all documents within 48 hours and there are no issues during underwriting. Trade-in values will need professional appraisal which takes 2-3 days. Can you commit to getting documents to me by Wednesday?",
            text_es: "Aprecio la urgencia. Aquí está mi evaluación honesta: conseguir un trato de $420K con trade-ins aprobado en 2 semanas es ambicioso pero posible SI recibimos todos los documentos dentro de 48 horas y no hay problemas durante la suscripción. Los valores de trade-in necesitarán avalúo profesional que toma 2-3 días. ¿Puedes comprometerte a enviarme los documentos para el miércoles?",
            correct: true,
            feedback_en: "Excellent! You were honest about the timeline challenges and set clear expectations.",
            feedback_es: "¡Excelente! Fuiste honesto sobre los desafíos del plazo y estableciste expectativas claras."
          },
          {
            text_en: "Two weeks should be no problem. We'll get it done.",
            text_es: "Dos semanas no debería ser problema. Lo lograremos.",
            correct: false,
            feedback_en: "Don't make timeline promises on complex deals. Set realistic expectations.",
            feedback_es: "No hagas promesas de plazos en tratos complejos. Establece expectativas realistas."
          },
          {
            text_en: "That's impossible. You'll need at least 6 weeks for a deal this size.",
            text_es: "Eso es imposible. Necesitarás al menos 6 semanas para un trato de este tamaño.",
            correct: false,
            feedback_en: "Don't immediately shut down. Explore how to make it work with conditions.",
            feedback_es: "No cierres la puerta inmediatamente. Explora cómo hacerlo funcionar con condiciones."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "There's one more thing... One of my trade-ins, the 2018 Freightliner, still has $15,000 owed on it. I thought it was paid off but I just checked. Is that a problem?",
        audio_es: "Hay una cosa más... Uno de mis trade-ins, el Freightliner 2018, todavía tiene $15,000 que debo. Pensé que estaba pagado pero acabo de verificar. ¿Eso es un problema?",
        options: [
          {
            text_en: "Thanks for clarifying that - it's better to know now. We can still work with this, but it changes the structure. We'd need to pay off that $15,000 from the trade-in value, which reduces your equity. So instead of $180K in trade equity, you'd have about $165K. That means financing around $435,000 instead of $420,000. I'll document this and recalculate the deal terms.",
            text_es: "Gracias por aclarar eso - es mejor saberlo ahora. Todavía podemos trabajar con esto, pero cambia la estructura. Necesitaríamos pagar esos $15,000 del valor del trade-in, lo cual reduce tu equity. Entonces en lugar de $180K en equity de trade, tendrías como $165K. Eso significa financiar alrededor de $435,000 en lugar de $420,000. Documentaré esto y recalcularé los términos del trato.",
            correct: true,
            feedback_en: "Perfect handling! You adapted to new information and explained the impact clearly.",
            feedback_es: "¡Manejo perfecto! Te adaptaste a nueva información y explicaste el impacto claramente."
          },
          {
            text_en: "That's a big problem. You lied to me. Now I don't know if I can trust anything you've said.",
            text_es: "Eso es un gran problema. Me mentiste. Ahora no sé si puedo confiar en algo de lo que has dicho.",
            correct: false,
            feedback_en: "Never accuse customers of lying. People genuinely forget or make mistakes.",
            feedback_es: "Nunca acuses a los clientes de mentir. Las personas genuinamente olvidan o cometen errores."
          },
          {
            text_en: "You'll need to pay that off before we can do anything with the trade-in.",
            text_es: "Necesitarás pagar eso antes de que podamos hacer algo con el trade-in.",
            correct: false,
            feedback_en: "Payoffs can be rolled into deals. Explore solutions, don't create barriers.",
            feedback_es: "Los pagos pendientes pueden incluirse en tratos. Explora soluciones, no crees barreras."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Okay, one final question. What if I can only get half the documents by Wednesday? My accountant is on vacation and I can't get the tax returns until next week.",
        audio_es: "Okay, una pregunta final. ¿Qué pasa si solo puedo conseguir la mitad de los documentos para el miércoles? Mi contador está de vacaciones y no puedo conseguir las declaraciones de impuestos hasta la próxima semana.",
        options: [
          {
            text_en: "Let's strategize this. Send everything you CAN get by Wednesday - bank statements, equipment info, business license. I can start the file and get preliminary feedback. For the tax returns, can your accountant email them from vacation, or do you have copies of last year's from when they were filed? We need to be creative to hit your timeline.",
            text_es: "Vamos a crear una estrategia. Envía todo lo que PUEDAS conseguir para el miércoles - estados de cuenta, información del equipo, licencia de negocio. Puedo empezar el archivo y obtener retroalimentación preliminar. Para las declaraciones de impuestos, ¿puede tu contador enviarlas por email desde vacaciones, o tienes copias del año pasado de cuando se presentaron? Necesitamos ser creativos para cumplir tu plazo.",
            correct: true,
            feedback_en: "Excellent problem-solving! You found ways to work around the obstacle.",
            feedback_es: "¡Excelente resolución de problemas! Encontraste formas de sortear el obstáculo."
          },
          {
            text_en: "Then we can't meet your timeline. You'll have to wait until you have everything.",
            text_es: "Entonces no podemos cumplir tu plazo. Tendrás que esperar hasta que tengas todo.",
            correct: false,
            feedback_en: "Be creative. There are often ways to work around document delays.",
            feedback_es: "Sé creativo. A menudo hay formas de sortear retrasos de documentos."
          },
          {
            text_en: "Just send whatever you can whenever you can. We'll make it work.",
            text_es: "Solo envía lo que puedas cuando puedas. Lo haremos funcionar.",
            correct: false,
            feedback_en: "Too vague. On time-sensitive deals, you need clear action plans.",
            feedback_es: "Muy vago. En tratos con plazos ajustados, necesitas planes de acción claros."
          }
        ]
      }
    ]
  },
  'fraud-detection': {
    id: 'fraud-detection',
    title_en: 'Fraud Red Flags',
    title_es: 'Banderas Rojas de Fraude',
    description_en: 'Identify potential fraud indicators during a customer call.',
    description_es: 'Identifica indicadores potenciales de fraude durante una llamada con cliente.',
    difficulty: 'hard',
    duration: 15,
    icon: '🚨',
    steps: [
      {
        type: 'customer',
        audio_en: "Hey, I need to finance a truck ASAP. Like, today if possible. I have cash for the down payment ready to go.",
        audio_es: "Oye, necesito financiar un camión LO ANTES POSIBLE. Como, hoy si es posible. Tengo efectivo para el enganche listo.",
        options: [
          {
            text_en: "I understand you're eager to move quickly. I can certainly help you get started today. What's driving the urgent timeline? And can you tell me a bit about your trucking business?",
            text_es: "Entiendo que estás ansioso por avanzar rápido. Ciertamente puedo ayudarte a empezar hoy. ¿Qué está impulsando el plazo urgente? ¿Y puedes contarme un poco sobre tu negocio de transporte?",
            correct: true,
            feedback_en: "Good! You acknowledged urgency while gathering information about the situation.",
            feedback_es: "¡Bien! Reconociste la urgencia mientras recopilabas información sobre la situación."
          },
          {
            text_en: "Great! Let me send you the application link right now!",
            text_es: "¡Genial! ¡Déjame enviarte el link de solicitud ahora mismo!",
            correct: false,
            feedback_en: "Extreme urgency is a fraud red flag. Always ask questions to understand why.",
            feedback_es: "La urgencia extrema es una bandera roja de fraude. Siempre pregunta para entender por qué."
          },
          {
            text_en: "That's suspicious. Why do you need it so fast?",
            text_es: "Eso es sospechoso. ¿Por qué lo necesitas tan rápido?",
            correct: false,
            feedback_en: "Don't accuse. Ask professional questions to understand the situation.",
            feedback_es: "No acuses. Haz preguntas profesionales para entender la situación."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Yeah, well, I just started my trucking company last month. But I already have a contract lined up that starts in 3 days. The truck I want is at a dealer 4 hours away. I'm paying $90,000 cash down on a $95,000 truck.",
        audio_es: "Sí, bueno, acabo de empezar mi compañía de transporte el mes pasado. Pero ya tengo un contrato alineado que empieza en 3 días. El camión que quiero está en un concesionario a 4 horas de distancia. Voy a pagar $90,000 de enganche en efectivo en un camión de $95,000.",
        options: [
          {
            text_en: "I want to make sure I understand - you're putting $90,000 down on a $95,000 truck, meaning you're only financing $5,000? That's unusual. Also, for a company that started last month, we would typically need to see personal financials. Can I ask how the business is structured and where the down payment funds are coming from?",
            text_es: "Quiero asegurarme de entender - ¿vas a poner $90,000 de enganche en un camión de $95,000, lo que significa que solo financiarías $5,000? Eso es inusual. Además, para una compañía que empezó el mes pasado, típicamente necesitaríamos ver estados financieros personales. ¿Puedo preguntar cómo está estructurado el negocio y de dónde vienen los fondos del enganche?",
            correct: true,
            feedback_en: "Excellent! Multiple red flags here: new business, huge down payment, tiny financing amount. You're probing appropriately.",
            feedback_es: "¡Excelente! Múltiples banderas rojas aquí: negocio nuevo, enganche enorme, monto pequeño a financiar. Estás investigando apropiadamente."
          },
          {
            text_en: "Perfect! With 95% down, this should be easy approval!",
            text_es: "¡Perfecto! ¡Con 95% de enganche, esto debería ser aprobación fácil!",
            correct: false,
            feedback_en: "HUGE red flag! Why would someone finance $5K when they have $90K cash? This smells like money laundering.",
            feedback_es: "¡ENORME bandera roja! ¿Por qué alguien financiaría $5K cuando tiene $90K en efectivo? Esto huele a lavado de dinero."
          },
          {
            text_en: "New businesses need 2 years of history. Come back then.",
            text_es: "Los negocios nuevos necesitan 2 años de historial. Regresa entonces.",
            correct: false,
            feedback_en: "While new businesses are harder to approve, you missed the bigger fraud indicators.",
            feedback_es: "Aunque los negocios nuevos son más difíciles de aprobar, perdiste los indicadores mayores de fraude."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "The money is from my uncle. He gave me cash to start the business. Look, I don't want a bunch of paperwork. Can't you just take my money and finance the small amount? I'll pay it off in a few months anyway.",
        audio_es: "El dinero es de mi tío. Me dio efectivo para empezar el negocio. Mira, no quiero un montón de papeleo. ¿No puedes solo tomar mi dinero y financiar la pequeña cantidad? Lo voy a pagar en unos meses de todos modos.",
        options: [
          {
            text_en: "I appreciate you explaining. However, for any financing, we have compliance requirements we must follow - this protects both you and us. Large cash down payments require documentation of the source of funds. I'd be happy to proceed if you can provide bank statements showing the transfer from your uncle, or a gift letter. This is standard for deals of this nature.",
            text_es: "Aprecio que expliques. Sin embargo, para cualquier financiamiento, tenemos requisitos de cumplimiento que debemos seguir - esto te protege a ti y a nosotros. Los enganches grandes en efectivo requieren documentación del origen de los fondos. Con gusto procedería si puedes proporcionar estados de cuenta mostrando la transferencia de tu tío, o una carta de regalo. Esto es estándar para tratos de esta naturaleza.",
            correct: true,
            feedback_en: "Perfect response! You maintained professionalism while enforcing compliance requirements.",
            feedback_es: "¡Respuesta perfecta! Mantuviste profesionalismo mientras aplicabas requisitos de cumplimiento."
          },
          {
            text_en: "Cash from your uncle with no documentation? Sure, that works.",
            text_es: "¿Efectivo de tu tío sin documentación? Claro, eso funciona.",
            correct: false,
            feedback_en: "NEVER accept undocumented cash! This could be money laundering and you could be criminally liable.",
            feedback_es: "¡NUNCA aceptes efectivo sin documentar! Esto podría ser lavado de dinero y podrías ser criminalmente responsable."
          },
          {
            text_en: "I think you're trying to launder money. I'm ending this call.",
            text_es: "Creo que estás tratando de lavar dinero. Voy a terminar esta llamada.",
            correct: false,
            feedback_en: "Don't accuse directly. Follow compliance procedures and let the experts investigate.",
            feedback_es: "No acuses directamente. Sigue los procedimientos de cumplimiento y deja que los expertos investiguen."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 The customer becomes agitated and says: 'Forget it! This is too much hassle. I'll just go to another lender who won't ask so many questions.' What do you do?",
        message_es: "📋 El cliente se agita y dice: '¡Olvídalo! Esto es demasiada molestia. Iré a otro prestamista que no haga tantas preguntas.' ¿Qué haces?",
        options: [
          {
            text_en: "Document everything about this call in the CRM immediately, including all red flags: new business, excessive cash, resistance to documentation, urgency. Flag this for compliance review. Do NOT try to retain this customer.",
            text_es: "Documentar todo sobre esta llamada en el CRM inmediatamente, incluyendo todas las banderas rojas: negocio nuevo, efectivo excesivo, resistencia a documentación, urgencia. Marcar esto para revisión de cumplimiento. NO trates de retener a este cliente.",
            correct: true,
            feedback_en: "Exactly right! When someone with fraud indicators walks away, let them go and document thoroughly.",
            feedback_es: "¡Exactamente correcto! Cuando alguien con indicadores de fraude se va, déjalo ir y documenta exhaustivamente."
          },
          {
            text_en: "Try to convince them to stay by offering to skip some documentation requirements.",
            text_es: "Tratar de convencerlos de quedarse ofreciendo saltarse algunos requisitos de documentación.",
            correct: false,
            feedback_en: "NEVER bend compliance rules! This could make you complicit in potential fraud.",
            feedback_es: "¡NUNCA dobles las reglas de cumplimiento! Esto podría hacerte cómplice de potencial fraude."
          },
          {
            text_en: "Call the police immediately and report suspected fraud.",
            text_es: "Llamar a la policía inmediatamente y reportar sospecha de fraude.",
            correct: false,
            feedback_en: "Escalate internally to compliance first. They decide if authorities need to be contacted.",
            feedback_es: "Escala internamente a cumplimiento primero. Ellos deciden si se necesita contactar autoridades."
          }
        ]
      }
    ]
  },
  'rate-negotiation': {
    id: 'rate-negotiation',
    title_en: 'Rate Negotiation',
    title_es: 'Negociación de Tasas',
    description_en: 'Customer wants to negotiate lower rates and threatens to go elsewhere.',
    description_es: 'El cliente quiere negociar tasas más bajas y amenaza con ir a otro lado.',
    difficulty: 'medium',
    duration: 12,
    icon: '📉',
    steps: [
      {
        type: 'customer',
        audio_en: "I got my approval back and the rate is 12.9%. That's way too high! I was expecting something around 6 or 7%. Can you do better?",
        audio_es: "Recibí mi aprobación y la tasa es 12.9%. ¡Eso es demasiado alto! Esperaba algo alrededor de 6 o 7%. ¿Pueden hacerlo mejor?",
        options: [
          {
            text_en: "I understand rate is important to you. Let me explain how the rate was determined and explore what options we might have. Your rate is based on several factors including credit history, time in business, and the equipment type. Can you tell me what other rates you've been quoted so I can understand what you're comparing to?",
            text_es: "Entiendo que la tasa es importante para ti. Déjame explicar cómo se determinó la tasa y explorar qué opciones podríamos tener. Tu tasa está basada en varios factores incluyendo historial crediticio, tiempo en el negocio y el tipo de equipo. ¿Puedes decirme qué otras tasas te han cotizado para entender con qué estás comparando?",
            correct: true,
            feedback_en: "Excellent! You acknowledged their concern and started gathering information to have an informed discussion.",
            feedback_es: "¡Excelente! Reconociste su preocupación y empezaste a recopilar información para tener una discusión informada."
          },
          {
            text_en: "12.9% is the best we can do. Take it or leave it.",
            text_es: "12.9% es lo mejor que podemos hacer. Tómalo o déjalo.",
            correct: false,
            feedback_en: "Never be dismissive. Always explore options and explain the reasoning.",
            feedback_es: "Nunca seas despectivo. Siempre explora opciones y explica el razonamiento."
          },
          {
            text_en: "6-7% is only for perfect credit. Your credit isn't good enough for that.",
            text_es: "6-7% es solo para crédito perfecto. Tu crédito no es suficiente para eso.",
            correct: false,
            feedback_en: "Don't be blunt about credit shortcomings. Explain professionally.",
            feedback_es: "No seas brusco sobre deficiencias de crédito. Explica profesionalmente."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Well, Bank of America pre-qualified me at 8.5%, but they can't do equipment financing. And my credit score is 680, which isn't bad! I don't understand why you're charging me so much more.",
        audio_es: "Bueno, Bank of America me pre-calificó al 8.5%, pero ellos no hacen financiamiento de equipo. ¡Y mi puntaje de crédito es 680, que no está mal! No entiendo por qué me están cobrando tanto más.",
        options: [
          {
            text_en: "Thanks for sharing that context. You're right that 680 isn't bad, but equipment financing rates differ from traditional bank loans because of the specialized nature and risk factors. However, let me check if there's anything we can do. Have you considered a larger down payment? That could potentially lower your rate. Also, if you have additional bank statements showing strong cash flow, I could ask for a rate review.",
            text_es: "Gracias por compartir ese contexto. Tienes razón en que 680 no está mal, pero las tasas de financiamiento de equipo difieren de los préstamos bancarios tradicionales por la naturaleza especializada y los factores de riesgo. Sin embargo, déjame verificar si hay algo que podamos hacer. ¿Has considerado un enganche mayor? Eso podría potencialmente bajar tu tasa. También, si tienes estados de cuenta adicionales mostrando fuerte flujo de caja, podría pedir una revisión de tasa.",
            correct: true,
            feedback_en: "Great response! You explained the rate difference and offered actionable ways to potentially improve terms.",
            feedback_es: "¡Gran respuesta! Explicaste la diferencia de tasa y ofreciste formas accionables de potencialmente mejorar los términos."
          },
          {
            text_en: "Equipment financing is different from bank loans. Our rate is what it is.",
            text_es: "El financiamiento de equipo es diferente de los préstamos bancarios. Nuestra tasa es la que es.",
            correct: false,
            feedback_en: "Too dismissive. Offer solutions or paths to better terms.",
            feedback_es: "Muy despectivo. Ofrece soluciones o caminos a mejores términos."
          },
          {
            text_en: "Let me talk to my manager and see if we can match Bank of America's rate.",
            text_es: "Déjame hablar con mi gerente y ver si podemos igualar la tasa de Bank of America.",
            correct: false,
            feedback_en: "Don't promise to match rates you likely can't match. Set realistic expectations.",
            feedback_es: "No prometas igualar tasas que probablemente no puedes igualar. Establece expectativas realistas."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "I can maybe do another $5,000 down. But honestly, if you can't get me under 10%, I'm going to go with another company. My buddy used Thunder Funding and got 9.9%.",
        audio_es: "Quizás puedo poner otros $5,000 de enganche. Pero honestamente, si no me pueden conseguir menos del 10%, voy a ir con otra compañía. Mi amigo usó Thunder Funding y le dieron 9.9%.",
        options: [
          {
            text_en: "I appreciate you being direct with me. Let me run the numbers with the additional $5,000 down and submit a rate exception request to our credit team. I can't promise we'll get to 9.9%, but I'll fight for the best rate possible. Can you give me until tomorrow afternoon? And just so you're comparing apples to apples - make sure Thunder Funding's quote doesn't have hidden fees or prepayment penalties that could cost you more in the long run.",
            text_es: "Aprecio que seas directo conmigo. Déjame calcular los números con los $5,000 adicionales de enganche y enviar una solicitud de excepción de tasa a nuestro equipo de crédito. No puedo prometer que llegaremos a 9.9%, pero pelearé por la mejor tasa posible. ¿Puedes darme hasta mañana por la tarde? Y solo para que compares manzanas con manzanas - asegúrate de que la cotización de Thunder Funding no tenga tarifas ocultas o penalidades por pago anticipado que podrían costarte más a largo plazo.",
            correct: true,
            feedback_en: "Perfect! You committed to action without overpromising, and educated them about comparing total costs.",
            feedback_es: "¡Perfecto! Te comprometiste a actuar sin prometer de más, y los educaste sobre comparar costos totales."
          },
          {
            text_en: "Go ahead and use Thunder Funding then. Good luck.",
            text_es: "Entonces adelante, usa Thunder Funding. Buena suerte.",
            correct: false,
            feedback_en: "Never give up on a deal without trying. Request rate exceptions when warranted.",
            feedback_es: "Nunca te rindas en un trato sin intentar. Solicita excepciones de tasa cuando sea justificado."
          },
          {
            text_en: "I can definitely get you 9.9% if you add that extra $5,000 down.",
            text_es: "Definitivamente puedo conseguirte 9.9% si agregas esos $5,000 extra de enganche.",
            correct: false,
            feedback_en: "Never guarantee rates you can't approve yourself. Under-promise, over-deliver.",
            feedback_es: "Nunca garantices tasas que no puedes aprobar tú mismo. Promete menos, entrega más."
          }
        ]
      },
      {
        type: 'system',
        message_en: "📋 RESULT: The credit team approves a rate reduction to 11.4% with the additional down payment. The customer asks: 'That's still higher than I wanted. What else can you do?'",
        message_es: "📋 RESULTADO: El equipo de crédito aprueba una reducción de tasa a 11.4% con el enganche adicional. El cliente pregunta: 'Eso todavía es más alto de lo que quería. ¿Qué más puedes hacer?'",
        options: [
          {
            text_en: "I understand. At 11.4%, your monthly payment would be approximately $X. One thing to consider - we offer early payoff without penalties. Many customers refinance at lower rates after 12-18 months of on-time payments. This builds your equipment financing history which helps get better rates in the future. Would you like me to show you what the payment looks like and discuss the path to refinancing?",
            text_es: "Entiendo. Al 11.4%, tu pago mensual sería aproximadamente $X. Algo a considerar - ofrecemos pago anticipado sin penalidades. Muchos clientes refinancian a tasas más bajas después de 12-18 meses de pagos a tiempo. Esto construye tu historial de financiamiento de equipo lo cual ayuda a conseguir mejores tasas en el futuro. ¿Te gustaría que te muestre cómo se ve el pago y discutamos el camino a refinanciar?",
            correct: true,
            feedback_en: "Excellent closing strategy! You showed the long-term path to better rates.",
            feedback_es: "¡Excelente estrategia de cierre! Mostraste el camino a largo plazo hacia mejores tasas."
          },
          {
            text_en: "That's the best I can do. Take it or leave it.",
            text_es: "Eso es lo mejor que puedo hacer. Tómalo o déjalo.",
            correct: false,
            feedback_en: "Always add value even when you can't reduce the rate further.",
            feedback_es: "Siempre agrega valor aunque no puedas reducir más la tasa."
          },
          {
            text_en: "Let me keep pushing and see if I can get it lower.",
            text_es: "Déjame seguir insistiendo y ver si puedo conseguirla más baja.",
            correct: false,
            feedback_en: "You already got an exception. Know when to pivot to other value propositions.",
            feedback_es: "Ya conseguiste una excepción. Sabe cuándo pivotar a otras propuestas de valor."
          }
        ]
      }
    ]
  },
  'competitor-comparison': {
    id: 'competitor-comparison',
    title_en: 'Competitor Comparison',
    title_es: 'Comparación con Competidores',
    description_en: 'Customer is comparing offers from multiple lenders.',
    description_es: 'El cliente está comparando ofertas de múltiples prestamistas.',
    difficulty: 'medium',
    duration: 12,
    icon: '⚖️',
    steps: [
      {
        type: 'customer',
        audio_en: "I'm shopping around and I've got quotes from 3 different companies. I want to make sure I'm getting the best deal. Can you tell me why I should choose Inspired Funding?",
        audio_es: "Estoy buscando opciones y tengo cotizaciones de 3 compañías diferentes. Quiero asegurarme de obtener la mejor oferta. ¿Puedes decirme por qué debería elegir Inspired Funding?",
        options: [
          {
            text_en: "That's smart to compare options! I'd be happy to help you evaluate. Rather than just telling you why we're great, let me understand what matters most to you. Is it the lowest rate, the lowest monthly payment, flexibility in terms, speed of funding, or customer service? Different lenders excel in different areas.",
            text_es: "¡Es inteligente comparar opciones! Con gusto te ayudo a evaluar. En lugar de solo decirte por qué somos geniales, déjame entender qué es lo más importante para ti. ¿Es la tasa más baja, el pago mensual más bajo, flexibilidad en términos, velocidad de financiamiento, o servicio al cliente? Diferentes prestamistas destacan en diferentes áreas.",
            correct: true,
            feedback_en: "Excellent consultative approach! You're positioning yourself as an advisor, not just a salesperson.",
            feedback_es: "¡Excelente enfoque consultivo! Te estás posicionando como un asesor, no solo un vendedor."
          },
          {
            text_en: "We're the best in the industry. Those other companies can't match our service.",
            text_es: "Somos los mejores en la industria. Esas otras compañías no pueden igualar nuestro servicio.",
            correct: false,
            feedback_en: "Vague claims don't help. Ask questions and provide specific value.",
            feedback_es: "Las afirmaciones vagas no ayudan. Haz preguntas y proporciona valor específico."
          },
          {
            text_en: "What rates did the other companies offer you? I'll try to beat them.",
            text_es: "¿Qué tasas te ofrecieron las otras compañías? Intentaré superarlas.",
            correct: false,
            feedback_en: "Don't immediately jump to rate competition. Understand their priorities first.",
            feedback_es: "No saltes inmediatamente a competir por tasa. Entiende sus prioridades primero."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Honestly, the rate is important but I'm most concerned about hidden fees. One company quoted me 10% but then I saw all these fees that added almost $3,000 to the deal. I don't want surprises.",
        audio_es: "Honestamente, la tasa es importante pero me preocupan más las tarifas ocultas. Una compañía me cotizó 10% pero luego vi todas estas tarifas que agregaron casi $3,000 al trato. No quiero sorpresas.",
        options: [
          {
            text_en: "I completely understand - transparency is crucial. Here's how our fees work: we have a documentation fee of $495 which covers all processing. There are no prepayment penalties, no hidden charges. Let me send you a complete breakdown in writing so you can compare apples to apples. Would it help if I also showed you the total cost over the life of the loan compared to the other offers?",
            text_es: "Entiendo completamente - la transparencia es crucial. Así es como funcionan nuestras tarifas: tenemos una tarifa de documentación de $495 que cubre todo el procesamiento. No hay penalidades por pago anticipado, no hay cargos ocultos. Déjame enviarte un desglose completo por escrito para que puedas comparar manzanas con manzanas. ¿Te ayudaría si también te mostrara el costo total durante la vida del préstamo comparado con las otras ofertas?",
            correct: true,
            feedback_en: "Perfect! You addressed their concern directly with specifics and offered to help them compare total costs.",
            feedback_es: "¡Perfecto! Abordaste su preocupación directamente con detalles específicos y ofreciste ayudarlos a comparar costos totales."
          },
          {
            text_en: "We don't have hidden fees. Don't worry about it.",
            text_es: "No tenemos tarifas ocultas. No te preocupes por eso.",
            correct: false,
            feedback_en: "Too dismissive. Show them exactly what the fees are to build trust.",
            feedback_es: "Muy despectivo. Muéstrales exactamente cuáles son las tarifas para construir confianza."
          },
          {
            text_en: "All companies have fees. That's just how the industry works.",
            text_es: "Todas las compañías tienen tarifas. Así es como funciona la industria.",
            correct: false,
            feedback_en: "Normalizing fees doesn't help. Differentiate with transparency.",
            feedback_es: "Normalizar las tarifas no ayuda. Diferénciate con transparencia."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "That would be helpful. Also, one of the companies said they can fund in 24 hours. You guys can't be that fast, right?",
        audio_es: "Eso sería útil. Además, una de las compañías dijo que pueden financiar en 24 horas. Ustedes no pueden ser tan rápidos, ¿verdad?",
        options: [
          {
            text_en: "Actually, we can fund quickly when everything aligns - I've seen deals close in 48-72 hours. The 24-hour claims usually come with conditions: perfect credit, small loan amounts, and everything submitted perfectly. What's your timeline looking like? If speed is critical, I can tell you exactly what we'd need and whether your deal qualifies for expedited processing.",
            text_es: "De hecho, podemos financiar rápido cuando todo se alinea - he visto tratos cerrar en 48-72 horas. Las promesas de 24 horas usualmente vienen con condiciones: crédito perfecto, montos pequeños de préstamo, y todo enviado perfectamente. ¿Cómo se ve tu plazo? Si la velocidad es crítica, puedo decirte exactamente qué necesitaríamos y si tu trato califica para procesamiento expedito.",
            correct: true,
            feedback_en: "Great response! You were honest while explaining the reality behind competitor claims.",
            feedback_es: "¡Gran respuesta! Fuiste honesto mientras explicabas la realidad detrás de las promesas de competidores."
          },
          {
            text_en: "24 hours is a lie. Nobody can fund that fast. Don't believe them.",
            text_es: "24 horas es mentira. Nadie puede financiar tan rápido. No les creas.",
            correct: false,
            feedback_en: "Don't directly attack competitors. It looks unprofessional.",
            feedback_es: "No ataques directamente a los competidores. Se ve poco profesional."
          },
          {
            text_en: "No, we usually take 5-7 business days. That's industry standard.",
            text_es: "No, usualmente tomamos 5-7 días hábiles. Eso es estándar en la industria.",
            correct: false,
            feedback_en: "You might be able to go faster. Don't undersell your capabilities.",
            feedback_es: "Podrías ser más rápido. No subestimes tus capacidades."
          }
        ]
      },
      {
        type: 'customer',
        audio_en: "Look, I'll be honest. Company B offered me a slightly lower rate but I've heard good things about Inspired Funding. If you can match their rate or come close, I'll go with you guys.",
        audio_es: "Mira, seré honesto. La Compañía B me ofreció una tasa ligeramente más baja pero he escuchado cosas buenas de Inspired Funding. Si pueden igualar su tasa o acercarse, iré con ustedes.",
        options: [
          {
            text_en: "I appreciate your honesty and that you've heard good things about us. What's the rate Company B offered? I can't promise to match it, but I can submit your deal for a competitive rate review. Also, beyond rate, consider this: what happens when you have a question at 6 PM on a Friday? Or need help mid-loan? Our service doesn't end at funding - and that has real value. Let me see what I can do on the rate.",
            text_es: "Aprecio tu honestidad y que hayas escuchado cosas buenas sobre nosotros. ¿Cuál es la tasa que ofreció la Compañía B? No puedo prometer igualarla, pero puedo enviar tu trato para una revisión de tasa competitiva. Además, más allá de la tasa, considera esto: ¿qué pasa cuando tienes una pregunta a las 6 PM un viernes? ¿O necesitas ayuda a mitad del préstamo? Nuestro servicio no termina al financiar - y eso tiene valor real. Déjame ver qué puedo hacer con la tasa.",
            correct: true,
            feedback_en: "Excellent! You asked for details, committed to trying, and reinforced value beyond rate.",
            feedback_es: "¡Excelente! Pediste detalles, te comprometiste a intentar, y reforzaste el valor más allá de la tasa."
          },
          {
            text_en: "Sure, I can match any rate. What did they offer?",
            text_es: "Claro, puedo igualar cualquier tasa. ¿Qué te ofrecieron?",
            correct: false,
            feedback_en: "Never promise to match without knowing the rate and checking with credit.",
            feedback_es: "Nunca prometas igualar sin conocer la tasa y verificar con crédito."
          },
          {
            text_en: "If they have a better rate, you should probably go with them.",
            text_es: "Si tienen una mejor tasa, probablemente deberías ir con ellos.",
            correct: false,
            feedback_en: "Never concede without trying! Fight for the deal.",
            feedback_es: "¡Nunca cedas sin intentar! Pelea por el trato."
          }
        ]
      }
    ]
  }
};

export default function InteractiveSimulation() {
  const navigate = useNavigate();
  const { simulationId } = useParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const audioRef = useRef(null);

  const lang = user?.training_language || 'es';
  const t = (en, es) => lang === 'es' ? es : en;
  
  const baseSimulation = SIMULATIONS[simulationId];
  
  // Shuffle options once when simulation loads (useMemo keeps it stable during the session)
  const simulation = useMemo(() => {
    if (!baseSimulation) return null;
    return shuffleSimulationOptions(baseSimulation);
  }, [simulationId, baseSimulation]);
  
  if (!simulation) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold mb-4">
          {t('Simulation not found', 'Simulación no encontrada')}
        </h2>
        <Button onClick={() => navigate('/training/sandbox')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back to Sandbox', 'Volver al Sandbox')}
        </Button>
      </div>
    );
  }

  const step = simulation.steps[currentStep];
  const progress = ((currentStep + 1) / simulation.steps.length) * 100;

  // Play customer voice using ElevenLabs
  const playCustomerVoice = async (text) => {
    if (isPlaying || isLoading) return;
    
    setIsLoading(true);
    setAudioError(null);
    
    try {
      const response = await axios.post(
        `${API}/training/tts/generate`,
        { 
          text: text,
          language: lang
        },
        { 
          responseType: 'blob',
          timeout: 60000
        }
      );
      
      // Create audio from blob
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onplay = () => {
        setIsPlaying(true);
        setIsLoading(false);
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setAudioError(t('Audio playback error', 'Error de reproducción de audio'));
        setIsPlaying(false);
        setIsLoading(false);
      };
      
      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setAudioError(t('Could not generate audio', 'No se pudo generar audio'));
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleOptionSelect = (index) => {
    if (showFeedback) return;
    setSelectedOption(index);
  };

  const handleSubmit = () => {
    if (selectedOption === null) return;
    
    const option = step.options[selectedOption];
    if (option.correct) {
      setScore(score + 1);
    }
    setShowFeedback(true);
  };

  const handleNext = () => {
    stopAudio();
    
    if (currentStep < simulation.steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      setCompleted(true);
    }
  };

  const restartSimulation = () => {
    stopAudio();
    setCurrentStep(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setScore(0);
    setCompleted(false);
  };

  const percentage = (score / simulation.steps.length) * 100;
  const passed = percentage >= 80;

  // Results screen
  if (completed) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className={passed ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" : "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"}>
          <CardHeader className="text-center">
            {passed ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <CardTitle className="text-2xl text-green-700">
                  🎉 {t('Excellent Work!', '¡Excelente Trabajo!')}
                </CardTitle>
              </>
            ) : (
              <>
                <XCircle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                <CardTitle className="text-2xl text-amber-700">
                  {t('Keep Practicing!', '¡Sigue Practicando!')}
                </CardTitle>
              </>
            )}
            <CardDescription>
              {lang === 'es' ? simulation.title_es : simulation.title_en}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="text-5xl font-bold">{percentage.toFixed(0)}%</p>
              <p className="text-muted-foreground">
                {score} / {simulation.steps.length} {t('correct responses', 'respuestas correctas')}
              </p>
            </div>

            <Progress value={percentage} className={`h-4 ${!passed ? '[&>div]:bg-amber-500' : ''}`} />

            <div className="flex gap-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/training/sandbox')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('Back to Sandbox', 'Volver al Sandbox')}
              </Button>
              <Button 
                className="flex-1"
                onClick={restartSimulation}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('Try Again', 'Intentar de Nuevo')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/training/sandbox')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Exit Simulation', 'Salir de Simulación')}
        </Button>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {simulation.icon} {lang === 'es' ? simulation.title_es : simulation.title_en}
        </Badge>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-muted-foreground mb-2">
          <span>{t('Step', 'Paso')} {currentStep + 1} / {simulation.steps.length}</span>
          <span>{t('Score', 'Puntaje')}: {score}/{currentStep}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Simulation Content */}
      <Card className="mb-6">
        <CardHeader>
          {step.type === 'customer' ? (
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-blue-100">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {t('Customer Says:', 'El Cliente Dice:')}
                  <Badge variant="outline">
                    <Volume2 className="h-3 w-3 mr-1" />
                    {t('Voice', 'Voz')}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {t('Listen to the customer and choose your response', 'Escucha al cliente y elige tu respuesta')}
                </CardDescription>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-purple-100">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  {t('Scenario', 'Escenario')}
                </CardTitle>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Customer/System Message */}
          <div className={`p-4 rounded-lg ${step.type === 'customer' ? 'bg-blue-50 border border-blue-200' : 'bg-purple-50 border border-purple-200'}`}>
            <p className="text-lg">
              {step.type === 'customer' 
                ? (lang === 'es' ? step.audio_es : step.audio_en)
                : (lang === 'es' ? step.message_es : step.message_en)
              }
            </p>
            
            {/* Audio Controls for Customer */}
            {step.type === 'customer' && (
              <div className="mt-4 flex items-center gap-3">
                <Button
                  variant={isPlaying ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (isPlaying) {
                      stopAudio();
                    } else {
                      playCustomerVoice(lang === 'es' ? step.audio_es : step.audio_en);
                    }
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('Generating...', 'Generando...')}
                    </>
                  ) : isPlaying ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      {t('Stop', 'Detener')}
                    </>
                  ) : (
                    <>
                      <Headphones className="h-4 w-4 mr-2" />
                      {t('Listen to Customer', 'Escuchar al Cliente')}
                    </>
                  )}
                </Button>
                {audioError && (
                  <span className="text-sm text-red-500">{audioError}</span>
                )}
                <span className="text-xs text-muted-foreground">
                  🎙️ ElevenLabs AI Voice
                </span>
              </div>
            )}
          </div>

          {/* Response Options */}
          <div className="space-y-3">
            <h4 className="font-medium">
              {t('Your Response:', 'Tu Respuesta:')}
            </h4>
            {step.options.map((option, index) => (
              <div
                key={index}
                onClick={() => handleOptionSelect(index)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedOption === index 
                    ? 'border-primary bg-primary/5' 
                    : 'border-transparent bg-muted/50 hover:bg-muted'
                } ${
                  showFeedback && option.correct 
                    ? 'border-green-500 bg-green-50' 
                    : ''
                } ${
                  showFeedback && selectedOption === index && !option.correct 
                    ? 'border-red-500 bg-red-50' 
                    : ''
                }`}
              >
                <p className="text-sm">
                  {lang === 'es' ? option.text_es : option.text_en}
                </p>
                
                {/* Feedback */}
                {showFeedback && selectedOption === index && (
                  <div className={`mt-3 p-2 rounded ${option.correct ? 'bg-green-100' : 'bg-red-100'}`}>
                    <div className="flex items-center gap-2">
                      {option.correct ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                      <span className={`text-sm font-medium ${option.correct ? 'text-green-700' : 'text-red-700'}`}>
                        {option.correct ? t('Correct!', '¡Correcto!') : t('Incorrect', 'Incorrecto')}
                      </span>
                    </div>
                    <p className="text-sm mt-1">
                      {lang === 'es' ? option.feedback_es : option.feedback_en}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        {!showFeedback ? (
          <Button 
            onClick={handleSubmit}
            disabled={selectedOption === null}
          >
            {t('Submit Response', 'Enviar Respuesta')}
          </Button>
        ) : (
          <Button onClick={handleNext}>
            {currentStep < simulation.steps.length - 1 
              ? t('Next Step', 'Siguiente Paso')
              : t('See Results', 'Ver Resultados')
            }
          </Button>
        )}
      </div>
    </div>
  );
}
